import { KpiDashboardService } from "@/services/kpi-dashboard.service";
import { markRaw } from "vue";

const state = () => ({
    widgets: [],
    loading: false,
    error: null,
    wholesaler_list: {
        data: [],
        total: 0,
        per_page: 0,
        page: 1,
        pages: 0,
        sort_by: null,
        sort_order: null,
        search: null,
        meta: {}
    },
    dealer_list: {
        data: [],
        total: 0,
        per_page: 0,
        page: 1,
        pages: 0,
        sort_by: null,
        sort_order: null,
        search: null,
        meta: {}
    },
    dashboard: null,
})

const getters = {
    widgets: (s) => s.widgets,
    widgetsLoading: (s) => s.loading,
    widgetsError: (s) => s.error,

    wholesalerList: (s) => s.wholesaler_list,
    wholesalerListData: (s) => {
        console.log(s)
        return s.wholesaler_list?.data
    },
    wholesalerListMeta: (s) => {
        const {
            total = 0,
            per_page = 20,
            page = 1,
            pages = 1,
            sort_by = "last_registration",
            sort_order = "desc",
            search = "",
            meta = {},
        } = s.wholesaler_list || {};
        return { total, per_page, page, pages, sort_by, sort_order, search, meta };
    },

    dealerList: (s) => s.dealerList,
    dealerListData: (s) => s.dealerList?.data,
    dealerListMeta: (s) => {
        const {
            total = 0,
            per_page = 20,
            page = 1,
            pages = 1,
            sort_by = "date",
            sort_order = "desc",
            search = "",
            meta = {},
        } = s.dealerList || {};
        return { total, per_page, page, pages, sort_by, sort_order, search, meta };
    },

    dashboardData: (s) => ({
        widgets: s.widgets,
        wholesaler_list: s.wholesaler_list,
        dealer_list: s.dealerList,
        loading: s.loading,
        error: s.error,
        raw: s.dashboard,
    }),
};

const mutations = {
    setWidgets(state, widgetsMap) {
        if (!widgetsMap || typeof widgetsMap !== "object") {
            state.widgets = [];
            return;
        }
        state.widgets = Object.values(widgetsMap)
            .map(w => {
                if (!w || typeof w !== "object") return null;
                const { signature, ...rest } = w;
                if (!signature) return null;
                return { signature, data: markRaw(rest) };
            })
            .filter(Boolean);
    },

    setWholesalerList(state, payload) {
        if (!payload || typeof payload !== "object") {
            state.wholesaler_list = {
                data: [],
                total: 0,
                per_page: 0,
                page: 1,
                pages: 0,
                sort_by: null,
                sort_order: null,
                search: null,
            };
            return;
        }
        const {
            data = [],
            total = 0,
            per_page = 0,
            page = 1,
            pages = 0,
            sort_by = null,
            sort_order = null,
            search = null,
            meta = {}
        } = payload;

        state.wholesaler_list = {
            data: Array.isArray(data) ? data : [],
            total,
            per_page,
            page,
            pages,
            sort_by,
            sort_order,
            search,
            meta
        };
    },

    setDealerList(state, payload) {
        if (!payload || typeof payload !== "object") {
            state.dealer_list = {
                data: [],
                total: 0,
                per_page: 0,
                page: 1,
                pages: 0,
                sort_by: null,
                sort_order: null,
                search: null,
            };
            return;
        }
        const {
            data = [],
            total = 0,
            per_page = 0,
            page = 1,
            pages = 0,
            sort_by = null,
            sort_order = null,
            search = null,
        } = payload;

        state.dealer_list = {
            data: Array.isArray(data) ? data : [],
            total,
            per_page,
            page,
            pages,
            sort_by,
            sort_order,
            search,
        };
    },

    setDashboardData(state, payload) {
        state.dashboard = payload || null;

        const {
            widgets: widgetsMap = {},
            wholesaler_list: wholesalerList = null,
            dealer_list: dealerList = null,
        } = payload || {};

        const makeWidget = (list, type) =>
            list ? { signature: list.meta?.list_type || type, data: list.data || [] } : null;

        const enrichedWidgets = {
            ...widgetsMap,
            ...(dealerList ? { dealer_list: makeWidget(dealerList, "dealer_list") } : {}),
            ...(wholesalerList ? { wholesaler_list: makeWidget(wholesalerList, "wholesaler_list") } : {}),
        };

        this.commit("kpiDashboard/setWidgets", enrichedWidgets);
        this.commit("kpiDashboard/setWholesalerList", wholesalerList);
        this.commit("kpiDashboard/setDealerList", dealerList);
    },


    setLoading(state, v) {
        state.loading = v;
    },
    setError(state, e) {
        state.error = e || null;
    },
    reset(state) {
        state.widgets = [];
        state.loading = false;
        state.error = null;
        state.wholesalerList = {
            data: [],
            total: 0,
            per_page: 0,
            page: 1,
            pages: 0,
            sort_by: null,
            sort_order: null,
            search: null,
        };
        state.dealerList = {
            data: [],
            total: 0,
            per_page: 0,
            page: 1,
            pages: 0,
            sort_by: null,
            sort_order: null,
            search: null,
        };
        state.dashboard = null;
    },
};

const actions = {
    async fetchDashboard({ commit }) {
        commit("setLoading", true);
        commit("setError", null);
        try {
            const data = await KpiDashboardService.fetchWidgets();
            commit("setDashboardData", data || null);
            return true;
        } catch (e) {
            commit(
                "setError",
                e?.response?.data?.message || e?.message || "Error loading dashboard"
            );
            commit("setDashboardData", null);
            return false;
        } finally {
            commit("setLoading", false);
        }
    },

    clearDashboard({ commit }) {
        commit("reset");
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
